import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import CompanyNavigation from './CompanyNavigation';
import CompanyFooter from './CompanyFooter';
import Icon from './icons';
import PDFExportButton from '../PDFExportButton';
import { services, waymining } from '../../data/services';
import '../../styles/Company.css';

const CompanyHome: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    document.title = 'Waymining — Mining Technology & Safety Services';
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to a section when arriving via /#section from another route.
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 0);
    }
  }, [location]);

  return (
    <div className="company-page" id="company-page-container">
      <CompanyNavigation scrolled={scrolled} />

      {/* Hero */}
      <section id="home" className="company-hero">
        <div className="company-hero-bg"></div>
        <div className="company-hero-bg-animation">
          {[...Array(16)].map((_, i) => (
            <div
              key={i}
              className="company-mining-icon"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 15}s`,
                fontSize: `${18 + Math.random() * 26}px`,
              }}
            >
              {i % 3 === 0 ? '🚛' : i % 3 === 1 ? '🏗️' : '⛏️'}
            </div>
          ))}
        </div>
        <div className="company-hero-content">
          <span className="company-eyebrow">Waymining</span>
          <h1>
            Mining Technology &amp; <span>Safety Solutions</span>
          </h1>
          <p>
            We help South African and Mozambican mining operations run safer and smarter — from
            collision avoidance and driver-fatigue detection to drone dust
            suppression, control-room modernization, industrial IT, and the
            Waymining FMS fleet management platform.
          </p>
          <div className="company-hero-actions">
            <a href="#services" className="company-btn-primary">
              Explore our services <ArrowRight size={18} />
            </a>
            <Link to={waymining.path} className="company-btn-secondary">
              Discover Waymining FMS
            </Link>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="company-about">
        <div className="company-container">
          <h2>Who we are</h2>
          <p>
            Waymining is a South African and Mozambican mining technology and
            services company. We bring together safety systems, automation,
            connectivity and software to modernize mining operations — delivering
            measurable improvements in safety, efficiency and cost.
          </p>
        </div>
      </section>

      {/* Impact */}
      <section id="impact" className="company-impact">
        <div className="company-container">
          <div className="company-impact-card">
            <div className="company-impact-grid">
              <div className="company-impact-item">
                <div className="company-impact-icon">
                  <Icon name="ShieldAlert" className="w-6 h-6" />
                </div>
                <h3>Layered Safety Systems</h3>
                <p>Collision avoidance, fatigue detection and access control working together on site.</p>
              </div>
              <div className="company-impact-item">
                <div className="company-impact-icon">
                  <Icon name="Radar" className="w-6 h-6" />
                </div>
                <h3>24/7 Real-Time Monitoring</h3>
                <p>Fleet, equipment and site data tracked continuously, not on a fixed schedule.</p>
              </div>
              <div className="company-impact-item">
                <div className="company-impact-icon">
                  <Icon name="BrainCircuit" className="w-6 h-6" />
                </div>
                <h3>AI-Driven Insights</h3>
                <p>Predictive analytics across fleet, plant and equipment operations.</p>
              </div>
              <div className="company-impact-item">
                <div className="company-impact-icon">
                  <Icon name="Globe" className="w-6 h-6" />
                </div>
                <h3>Multi-Country Coverage</h3>
                <p>Operating across South Africa and Mozambique, built to scale to more sites.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="company-services">
        <div className="company-container">
          <div className="company-section-header">
            <h2>Products &amp; Services</h2>
            <p>A complete portfolio for safer, smarter mining operations.</p>
          </div>

          <div className="company-grid">
            {/* Waymining FMS product highlight */}
            <Link to={waymining.path} className="company-card company-card--product">
              <div className="company-card-icon">
                <Icon name={waymining.icon} className="w-8 h-8" />
              </div>
              <span className="company-card-tag">Product</span>
              <h3>{waymining.name}</h3>
              <p>{waymining.tagline}</p>
              <span className="company-card-link">
                View product <ArrowRight size={16} />
              </span>
            </Link>

            {services.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="company-card"
              >
                <div className="company-card-icon">
                  <Icon name={s.icon} className="w-8 h-8" />
                </div>
                <span className="company-card-tag">Service</span>
                <h3>{s.name}</h3>
                <p>{s.summary}</p>
                <span className="company-card-link">
                  Learn more <ArrowRight size={16} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section id="partners" className="company-partners">
        <div className="company-container">
          <div className="company-section-header">
            <h2>Our Partners</h2>
            <p>We team up with world-class mining technology providers to deliver best-in-class solutions.</p>
          </div>

          <div className="company-partner-card">
            <span className="company-card-tag">Technology Partner</span>
            <h3>HME Solutions</h3>
            <p>
              An Australia-based mining technology specialist delivering fleet management systems,
              predictive analytics, precision mining (payload systems and machine guidance), and
              mining communication infrastructure for open pit and underground operations worldwide.
              Together, we bring fully integrated hardware, software and support to modernize fleet
              and production operations end to end.
            </p>
            <a href="#services" className="company-card-link">
              See these offerings in our services <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      <CompanyFooter />
      <PDFExportButton
        variant="floating"
        filename="waymining-company-overview"
        elementId="company-page-container"
      />
    </div>
  );
};

export default CompanyHome;
